import React from 'react';
import PropTypes from 'prop-types';
import { utils } from '../../../utils/utils'

// Material UI
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import Tooltip from '@mui/material/Tooltip';
import Checkbox from '@mui/material/Checkbox';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import CardContent from '@mui/material/CardContent';

// Icons
import EditIcon from '@mui/icons-material/Edit';
import BlockIcon from '@mui/icons-material/Block';
import CheckIcon from '@mui/icons-material/Check';
import DeleteIcon from '@mui/icons-material/Delete';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import VisibilityIcon from '@mui/icons-material/Visibility';

// Components
import useStyles from './styles';
import IconCheck from '../../iconCheck';
import ChipState from '../../ChipState';
import { ReferenceArea } from 'recharts';

const Item = ({
    columns, row, onUpdate, onDelete, onView, onCopy, onSelect, isSelected, align, showActiveInactive,
    onShowUpdate, onShowDelete, customCells, lastRowStyle
}) => {
    const { classes } = useStyles({ align });

    const ValueComponent = ({ field, altField, translate, type, alt, tooltip, align }) => {
        const DefaultValue = React.forwardRef(({ value, type }, ref) => {
            // Si es un vinculo (probablemente el link de una imagen en un blob)
            if (type === 'image')
                return (
                    <div ref={ReferenceArea} className={classes.cardIconWrapper}>
                        <img src={value} alt={alt || "item"} className={classes.cardIcon}></img>
                    </div>
                )

            if (type === 'state')
                return <ChipState ref={ref} state={row.state} label={value} />

            return (
                <Typography ref={ref} variant="body1" component="p" className={classes.itemTitle} align={align} style={lastRowStyle}>
                    {
                        type === 'date'
                            ? utils.formatDate(value)
                            : type === 'shortDate'
                                ? utils.formatShortDate(value)
                                : type === 'boolean'
                                    ? <IconCheck value={value} />
                                    : type === 'money'
                                        ? utils.formatMoney(value)
                                        : value
                    }
                </Typography>
            )
        })

        let value = utils.getValueWhitPath(row, field);

        if (value === undefined || value === null)
            value = utils.getValueWhitPath(row, altField);

        if (translate)
            value = translate(value ?? row)

        if (tooltip)
            return (
                <Tooltip title={tooltip(row)} aria-label={field} placement='bottom-start'>
                    <div><DefaultValue value={value} type={type} /></div>
                </Tooltip>
            )

        // Default
        return <DefaultValue value={value} type={type} />
    }

    return (
        <Card>
            <CardContent>
                <Grid container alignItems='center' spacing={1}>
                    {
                        onSelect &&
                        <Grid item size={0.5}>
                            <Checkbox
                                color="primary"
                                checked={isSelected(row)}
                                onChange={(e, checked) => onSelect(checked, row)}
                                style={{ marginRight: '20px' }}
                            />
                        </Grid>
                    }
                    {
                        columns.map((column, i) => {
                            let columnSize;
                            switch (column.header?.toLowerCase()) {
                                case 'master id':
                                    columnSize = 1;
                                    break;
                                case 'id':
                                    columnSize = 1;
                                    break;
                                case 'codigo de producto':
                                    columnSize = 3;
                                    break;
                                case 'color':
                                    columnSize = 1;
                                    break;
                                case 'descripcion':
                                    columnSize = (onView || onCopy || onDelete || onUpdate || customCells) ? 3 : 4.5;
                                    break;
                                case 'cantidad':
                                    columnSize = 1;
                                    break;
                                default:
                                    columnSize = 2;
                            }

                            return (
                                <Grid key={column.header} item size={columnSize}>
                                    {
                                        column.type !== 'array'
                                            ? <ValueComponent {...column} />
                                            : column.value.map((v, i) =>
                                                <ValueComponent key={`${v}-${i}`} {...column} value={v} />)
                                    }
                                </Grid>
                            );
                        })
                    }
                    {
                        (!!onView || !!onCopy || !!onDelete || !!onUpdate || !!customCells) &&

                        <Grid item size={1.5}>
                            <Box display='flex' justifyContent='flex-end'>
                                {
                                    onView &&
                                    <Tooltip title='Ver' aria-label='ver'>
                                        <IconButton onClick={() => onView(row)} size="large">
                                            <VisibilityIcon />
                                        </IconButton>
                                    </Tooltip>
                                }
                                {
                                    onCopy &&
                                    <Tooltip title='Copiar' aria-label='copiar'>
                                        <IconButton onClick={() => onCopy(row)} size="large">
                                            <FileCopyIcon className={classes.copy} />
                                        </IconButton>
                                    </Tooltip>
                                }
                                {
                                    onShowUpdate(row) && onUpdate &&
                                    <Tooltip title='Editar' aria-label='editar'>
                                        <IconButton color="primary" onClick={() => onUpdate(row)} size="large">
                                            <EditIcon />
                                        </IconButton>
                                    </Tooltip>
                                }
                                {
                                    onShowDelete(row) && onDelete && !showActiveInactive
                                        ? <Tooltip title='Eliminar' aria-label='eliminar'>
                                            <IconButton onClick={() => onDelete(row)} size="large">
                                                <DeleteIcon />
                                            </IconButton>
                                        </Tooltip>
                                        : showActiveInactive
                                            ? row.active
                                                ? <Tooltip title='Dejar inactivo' aria-label='Dejar inactivo'>
                                                    <IconButton onClick={() => onDelete(row)} size="large">
                                                        <BlockIcon style={{ color: '#f44336' }} />
                                                    </IconButton>
                                                </Tooltip>
                                                : <Tooltip title='Dejar activo' aria-label='Dejar activo'>
                                                    <IconButton onClick={() => onDelete(row)} size="large">
                                                        <CheckIcon style={{ color: 'rgb(76, 175, 80)' }} />
                                                    </IconButton>
                                                </Tooltip>
                                            : null

                                }
                                {customCells && customCells(row)}
                            </Box>
                        </Grid>
                    }
                </Grid>
            </CardContent>
        </Card>
    );
}

Item.defaultProps = {
    columns: [],
    row: {},
    onShowDelete: () => true,
    onShowUpdate: () => true
}

Item.prototype = {
    columns: PropTypes.arrayOf(
        PropTypes.shape({
            field: PropTypes.string.isRequired,
            header: PropTypes.string.isRequired,
            type: PropTypes.string.isRequired,
            tooltip: PropTypes.func,
            alt: PropTypes.string,
            align: PropTypes.string,
        })).isRequired,
    row: PropTypes.object,
    onView: PropTypes.func,
    onUpdate: PropTypes.func,
    onDelete: PropTypes.func,
    onShowUpdate: PropTypes.func,
    onShowDelete: PropTypes.func,
    customCells: PropTypes.func,
    onCopy: PropTypes.func
}

export default Item;